import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Calendar,
  BarChart3,
  Scissors,
  Users,
  Sparkles,
  Check,
  ArrowRight,
  ChevronRight,
  Globe,
  Play,
  Star,
  Shield,
  Clock,
  Flame,
  Music,
  Instagram,
  Youtube,
  Linkedin,
  Twitter,
  Facebook,
  Pin,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Platform data for the logo strip
// ---------------------------------------------------------------------------
const platforms = [
  { name: 'TikTok',    icon: Music,     color: '#000000', supported: true },
  { name: 'Instagram', icon: Instagram,  color: '#E4405F', supported: true },
  { name: 'YouTube',   icon: Youtube,    color: '#FF0000', supported: true },
  { name: 'LinkedIn',  icon: Linkedin,   color: '#0A66C2', supported: true },
  { name: 'Twitter',   icon: Twitter,    color: '#1DA1F2', supported: false },
  { name: 'Facebook',  icon: Facebook,   color: '#1877F2', supported: false },
  { name: 'Pinterest', icon: Pin,        color: '#E60023', supported: false },
];

// ---------------------------------------------------------------------------
// Features data
// ---------------------------------------------------------------------------
const features = [
  {
    icon: Globe,
    title: 'Multi-Platform Posting',
    description:
      'Publish to TikTok, Instagram, YouTube, LinkedIn, and more from a single dashboard. One post, every platform.',
  },
  {
    icon: Sparkles,
    title: 'AI Captions',
    description:
      'Generate scroll-stopping captions, hashtags, and hooks powered by Claude. Optimized per platform automatically.',
  },
  {
    icon: Calendar,
    title: 'Content Calendar',
    description:
      'Drag-and-drop visual calendar. Plan weeks ahead, see your full pipeline at a glance, and never miss a posting window.',
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description:
      'Track impressions, engagement, and follower growth across every connected platform with unified reporting.',
  },
  {
    icon: Scissors,
    title: 'Video Clipping',
    description:
      'Clip highlights from long-form videos into platform-ready shorts. Repurpose one video into a week of content.',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description:
      'Invite your team, assign roles, approve content, and keep everyone aligned with shared workflows.',
  },
];

// ---------------------------------------------------------------------------
// Pricing tiers
// ---------------------------------------------------------------------------
const tiers = [
  {
    name: 'Free',
    price: 0,
    description: 'For creators getting started',
    features: [
      '2 connected platforms',
      '10 scheduled posts / month',
      '10 GB media storage',
      '5 AI caption credits',
      'Basic analytics',
    ],
    cta: 'Get Started Free',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: 29,
    description: 'For serious creators & small brands',
    features: [
      '7 connected platforms',
      '200 scheduled posts / month',
      '50 GB media storage',
      '200 AI caption credits',
      'Advanced analytics',
      'Video clipping tools',
      'Priority support',
    ],
    cta: 'Start Pro Trial',
    highlighted: true,
  },
  {
    name: 'Team',
    price: 79,
    description: 'For agencies & growing teams',
    features: [
      '7 connected platforms',
      'Unlimited scheduled posts',
      '500 GB media storage',
      'Unlimited AI credits',
      'Advanced analytics',
      'Video clipping tools',
      'Team roles & approvals',
      'Dedicated support',
    ],
    cta: 'Start Team Trial',
    highlighted: false,
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Landing() {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const multiplier = billingCycle === 'yearly' ? 0.8 : 1;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* ------------------------------------------------------------------ */}
      {/* NAV                                                                */}
      {/* ------------------------------------------------------------------ */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0a]/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-6 py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-sky-400 to-blue-600">
              <Flame className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">
              FLARE<span className="text-sky-400"> Social</span>
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#platforms" className="hover:text-white transition-colors">Platforms</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </div>

          {/* CTA */}
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-flex text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-400 transition-colors"
            >
              Get Started Free
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ------------------------------------------------------------------ */}
      {/* HERO                                                               */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative pt-36 pb-24 sm:pt-44 sm:pb-32">
        {/* Background glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-sky-500/10 blur-[128px]" />
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/5 px-4 py-1.5 text-xs font-medium text-sky-400">
            <Zap className="h-3.5 w-3.5" />
            Now with AI-powered captions
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.08] tracking-tight">
            Schedule smarter.
            <br />
            <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              Grow faster.
            </span>
          </h1>

          <p className="mt-6 mx-auto max-w-2xl text-lg sm:text-xl text-gray-400 leading-relaxed">
            Publish to TikTok, Instagram, YouTube, LinkedIn, and more from one
            dashboard. AI-crafted captions, a visual calendar, and real-time
            analytics -- everything you need to own social.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-sky-500/25 hover:bg-sky-400 transition-all hover:shadow-sky-400/30"
            >
              Get Started Free
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-7 py-3.5 text-base font-semibold text-gray-200 hover:bg-white/10 transition-all"
            >
              See Plans
              <ChevronRight className="h-5 w-5" />
            </a>
          </div>

          {/* Social proof mini strip */}
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-sky-500" /> No credit card required
            </span>
            <span className="hidden sm:block h-4 w-px bg-white/10" />
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-sky-500" /> Set up in 2 minutes
            </span>
            <span className="hidden sm:block h-4 w-px bg-white/10" />
            <span className="flex items-center gap-1.5">
              <Star className="h-4 w-4 text-sky-500" /> Loved by 1,000+ creators
            </span>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* PLATFORM LOGOS                                                     */}
      {/* ------------------------------------------------------------------ */}
      <section id="platforms" className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <p className="text-sm font-medium uppercase tracking-widest text-gray-500 mb-10">
            Publish everywhere your audience lives
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {platforms.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.name} className="group relative flex flex-col items-center gap-2">
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl border transition-all ${
                      p.supported
                        ? 'border-white/10 bg-white/5 group-hover:border-sky-500/40 group-hover:bg-sky-500/5'
                        : 'border-white/5 bg-white/[0.02] opacity-40'
                    }`}
                  >
                    <Icon className="h-6 w-6" style={{ color: p.supported ? p.color : '#555' }} />
                  </div>
                  <span className={`text-xs font-medium ${p.supported ? 'text-gray-300' : 'text-gray-600'}`}>
                    {p.name}
                  </span>
                  {!p.supported && (
                    <span className="absolute -top-2 -right-3 rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-sky-400 leading-none">
                      Soon
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* FEATURES                                                           */}
      {/* ------------------------------------------------------------------ */}
      <section id="features" className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest text-sky-400 mb-3">
              Features
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Everything you need to dominate social
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-gray-400">
              From scheduling to analytics, FLARE gives creators and teams the
              full toolkit to grow across every platform.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group rounded-2xl border border-white/5 bg-white/[0.02] p-7 hover:border-sky-500/20 hover:bg-sky-500/[0.03] transition-all"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-sky-500/10 text-sky-400 group-hover:bg-sky-500/20 transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-400">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* PRICING                                                            */}
      {/* ------------------------------------------------------------------ */}
      <section id="pricing" className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-sky-400 mb-3">
              Pricing
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-gray-400">
              Start free, upgrade when you're ready. No hidden fees.
            </p>

            {/* Billing toggle */}
            <div className="mt-8 inline-flex items-center rounded-full border border-white/10 bg-white/5 p-1 text-sm">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`rounded-full px-5 py-1.5 font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-sky-500 text-white shadow'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`rounded-full px-5 py-1.5 font-medium transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-sky-500 text-white shadow'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Yearly
                <span className="ml-1.5 text-[11px] text-sky-300 font-semibold">-20%</span>
              </button>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {tiers.map((tier) => {
              const displayPrice = tier.price === 0 ? 0 : Math.round(tier.price * multiplier);
              return (
                <div
                  key={tier.name}
                  className={`relative flex flex-col rounded-2xl border p-8 transition-all ${
                    tier.highlighted
                      ? 'border-sky-500/40 bg-sky-500/[0.04] shadow-lg shadow-sky-500/5'
                      : 'border-white/5 bg-white/[0.02]'
                  }`}
                >
                  {tier.highlighted && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-sky-500 px-4 py-1 text-xs font-bold uppercase tracking-wider text-white">
                      Most Popular
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-bold">{tier.name}</h3>
                    <p className="mt-1 text-sm text-gray-400">{tier.description}</p>
                  </div>

                  <div className="mb-8">
                    <span className="text-5xl font-extrabold tracking-tight">
                      ${displayPrice}
                    </span>
                    <span className="text-gray-500 text-sm">/mo</span>
                  </div>

                  <ul className="mb-10 flex-1 space-y-3.5">
                    {tier.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-3 text-sm text-gray-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                        {feat}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/signup"
                    className={`block w-full rounded-xl py-3 text-center text-sm font-semibold transition-all ${
                      tier.highlighted
                        ? 'bg-sky-500 text-white hover:bg-sky-400 shadow-lg shadow-sky-500/20'
                        : 'border border-white/10 bg-white/5 text-gray-200 hover:bg-white/10'
                    }`}
                  >
                    {tier.cta}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* CTA BANNER                                                         */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Ready to grow your audience?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join thousands of creators and teams using FLARE Social to schedule
            smarter, create faster, and reach more people.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-sky-500/25 hover:bg-sky-400 transition-all"
          >
            Get Started Free
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* FOOTER                                                             */}
      {/* ------------------------------------------------------------------ */}
      <footer className="border-t border-white/5 py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {/* Brand */}
            <div>
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-400 to-blue-600">
                  <Flame className="h-4 w-4 text-white" />
                </div>
                <span className="text-lg font-bold tracking-tight">
                  FLARE<span className="text-sky-400"> Social</span>
                </span>
              </Link>
              <p className="text-sm text-gray-500 leading-relaxed">
                Multi-platform social scheduling and publishing by AKA Industries.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-gray-300">Product</h4>
              <ul className="space-y-2.5 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#platforms" className="hover:text-white transition-colors">Integrations</a></li>
                <li><a href="mailto:api@flare-social.com" className="hover:text-white transition-colors">API Access</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-gray-300">Company</h4>
              <ul className="space-y-2.5 text-sm text-gray-500">
                <li><a href="https://akaind.ca" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">AKA Industries</a></li>
                <li><a href="mailto:hello@flare-social.com" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="https://akaind.ca/blog" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-gray-300">Legal</h4>
              <ul className="space-y-2.5 text-sm text-gray-500">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/5 pt-8">
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} AKA Industries. All rights reserved.
            </p>
            <p className="text-xs text-gray-600">
              Built with care in Canada
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
